import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIList from 'UIComponents/list/UIList';
import UISection from 'UIComponents/section/UISection';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UILink from 'UIComponents/link/UILink';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { WarningIcon } from 'foundations-assets';
import { View } from 'foundations-components/components/View';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_DESCRIPTION = 'epIssueButton.badgeDescription';
const Issues = ({
  issues,
  iconBgColor,
  issueLabel,
  onIssueClick,
  isListStyled = true
}) => {
  return /*#__PURE__*/_jsxs(UISection, {
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(UIMediaObject, {
        className: "m-left-8",
        itemLeft: /*#__PURE__*/_jsx(IconCircle, {
          icon: WarningIcon,
          size: "100",
          padding: "100",
          color: "icon-core-on-fill-default",
          backgroundColor: iconBgColor
        }),
        children: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: issueLabel
          })
        })
      })
    }), /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(List, {
        ordered: true,
        styled: isListStyled,
        children: issues.map((issue, index) => {
          const {
            node
          } = issue;
          return /*#__PURE__*/_jsx(ListItem, {
            marginInlineStart: "200",
            marginInlineEnd: "200",
            children: /*#__PURE__*/_jsx(UILink, {
              use: "unstyled",
              onClick: () => onIssueClick(issue),
              children: node
            })
          }, index);
        })
      })
    })]
  });
};
const IssueButtonWrapper = styled.div.withConfig({
  displayName: "EPIssueButton__IssueButtonWrapper",
  componentId: "sc-17iax4a-0"
})(["white-space:nowrap;"]);
function transformPopoverWidthToString(popoverWidth) {
  if (typeof popoverWidth === 'number') {
    return `${popoverWidth}px`;
  }
  return;
}
const EPIssueButton = ({
  actions,
  buttonSize = 'small',
  children,
  'data-test': dataTest,
  disabled = false,
  errorLabel = 'epIssueButton.errorLabel',
  errors,
  loading = false,
  loadingFailed = false,
  onButtonClick,
  onDropdownOpenComplete,
  onIssueClick,
  onMouseEnter,
  popoverPlacement = 'bottom left',
  popoverWidth = 300,
  popoverInlineSize,
  tooltipOverride,
  warningLabel = 'epIssueButton.warningLabel',
  warnings,
  isListStyled = true
}) => {
  const [canPopupOpen, setCanPopupOpen] = useState(false);
  function handleButtonClick() {
    setCanPopupOpen(false);
    onButtonClick();
  }
  function hasIssues() {
    return !!errors.length || !!warnings.length;
  }
  function handleMouseEnterButton() {
    if (hasIssues()) {
      setCanPopupOpen(true);
    }
    if (onMouseEnter) {
      onMouseEnter();
    }
  }
  function renderPopOverContent() {
    return /*#__PURE__*/_jsxs(View, {
      overflowY: "auto",
      maxBlockSize: "50vh",
      marginBlockStart: "800",
      children: [!!errors.length && /*#__PURE__*/_jsx(Issues, {
        issues: errors,
        iconBgColor: "fill-alert-default",
        issueLabel: errorLabel,
        onIssueClick: onIssueClick,
        isListStyled: isListStyled
      }), !!warnings.length && /*#__PURE__*/_jsx(Issues, {
        issues: warnings,
        iconBgColor: "fill-caution-default",
        issueLabel: warningLabel,
        onIssueClick: onIssueClick
      })]
    });
  }
  function renderIssueButton() {
    return /*#__PURE__*/_jsx(IssueButtonWrapper, {
      children: /*#__PURE__*/_jsx(NotificationBadge, {
        badgeDescription: I18n.text(BADGE_DESCRIPTION),
        badgeLabel: errors.length,
        showBadge: !!errors.length && !tooltipOverride,
        "data-test": dataTest,
        "data-test-num-errors": errors.length,
        children: /*#__PURE__*/_jsxs(ButtonGroup, {
          children: [/*#__PURE__*/_jsx(LoadingButton, {
            disabled: !!errors.length || disabled,
            use: "primary",
            size: buttonSize,
            onClick: handleButtonClick,
            onMouseEnter: handleMouseEnterButton,
            loading: loading,
            failed: loadingFailed,
            children: children
          }), actions && actions.length > 0 && /*#__PURE__*/_jsx(Dropdown, {
            "data-test-id": "issue-dropdown",
            buttonUse: "primary",
            buttonText: /*#__PURE__*/_jsx(UIIcon, {
              name: "down"
            }),
            buttonStyles: {
              paddingInline: '100',
              // HACK: Remove once https://git.hubteam.com/HubSpot/foundations-components/issues/5632 is addressed
              maxBlockSize: '32px'
            },
            buttonSize: buttonSize,
            caretRenderer: null,
            disabled: !!errors.length || disabled || loading,
            onOpenComplete: onDropdownOpenComplete,
            dropdownStyles: {
              borderInlineStartColor: 'border-core-default',
              borderInlineStartWidth: '100',
              borderInlineStartStyle: 'solid'
            },
            children: /*#__PURE__*/_jsx(UIList, {
              children: actions.map((action, index) => {
                const {
                  label,
                  onClick,
                  id
                } = action;
                return /*#__PURE__*/_jsx(UIButton, {
                  "data-test-id": id,
                  onClick: onClick,
                  children: label
                }, index);
              })
            })
          })]
        })
      })
    });
  }
  if (tooltipOverride) {
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: popoverPlacement,
      title: tooltipOverride,
      children: renderIssueButton()
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: canPopupOpen && hasIssues(),
    closeOnOutsideClick: true,
    onOpenChange: () => setCanPopupOpen(false),
    onFocusLeave: () => setCanPopupOpen(false),
    placement: popoverPlacement,
    inlineSize: popoverInlineSize !== null && popoverInlineSize !== void 0 ? popoverInlineSize : transformPopoverWidthToString(popoverWidth),
    autoPlacement: false,
    Content: renderPopOverContent,
    "data-test-id": "issue-popover",
    children: renderIssueButton()
  });
};
EPIssueButton.propTypes = {
  actions: PropTypes.array,
  buttonSize: PropTypes.string,
  children: PropTypes.node.isRequired,
  'data-test': PropTypes.string,
  disabled: PropTypes.bool,
  errorLabel: PropTypes.string,
  errors: PropTypes.array.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onDropdownOpenComplete: PropTypes.func,
  onIssueClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  popoverPlacement: PropTypes.string,
  popoverWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipOverride: PropTypes.node,
  warningLabel: PropTypes.string,
  warnings: PropTypes.array.isRequired
};
export default EPIssueButton;